import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmt2 } from "@/lib/utils";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader, Panel, AllergenTags } from "@/components/ui-bits";
import DataIOToolbar from "@/components/DataIOToolbar";

const ALLERGENS = [
  "Gluten",
  "Wheat",
  "Egg",
  "Milk",
  "Soy",
  "Peanut",
  "Tree nut",
  "Sesame",
  "Mustard",
  "Celery",
  "Sulphites",
  "Fish",
  "Crustaceans",
  "Molluscs",
  "Lupin",
];

const PACK_TYPES = ["bag", "sack", "tub", "bottle", "drum", "jar", "carton", "tray", "unit"];

const empty = {
  name: "",
  sku: "",
  unit: "kg",
  category: "",
  allergens: [],
  min_stock: 0,
  notes: "",
  default_pack_count: "",
  default_pack_type: "",
  default_pack_size: "",
  default_pack_unit: "",
};

export default function Materials() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => setRows((await api.get("/materials")).data);
  useEffect(() => {
    load();
    // Mount-only fetch; `load` is a stable closure and intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Alphabetise (case-insensitive) then filter by name/sku/category — the
  // three columns operators typically scan when hunting a material.
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...rows].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
    );
    if (!q) return sorted;
    return sorted.filter(
      (m) =>
        (m.name || "").toLowerCase().includes(q) ||
        (m.sku || "").toLowerCase().includes(q) ||
        (m.category || "").toLowerCase().includes(q),
    );
  }, [rows, search]);

  const startNew = () => {
    setForm(empty);
    setEditId(null);
    setOpen(true);
  };
  const startEdit = (m) => {
    setForm({
      name: m.name,
      sku: m.sku || "",
      unit: m.unit,
      category: m.category || "",
      allergens: m.allergens || [],
      min_stock: m.min_stock || 0,
      notes: m.notes || "",
      default_pack_count: m.default_pack_count ?? "",
      default_pack_type: m.default_pack_type || "",
      default_pack_size: m.default_pack_size ?? "",
      default_pack_unit: m.default_pack_unit || "",
    });
    setEditId(m.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = {
      ...form,
      min_stock: Number(form.min_stock || 0),
      default_pack_count:
        form.default_pack_count === "" ? null : Number(form.default_pack_count),
      default_pack_size:
        form.default_pack_size === "" ? null : Number(form.default_pack_size),
      default_pack_type: form.default_pack_type || null,
      default_pack_unit: form.default_pack_unit || null,
    };
    try {
      if (editId) await api.patch(`/materials/${editId}`, body);
      else await api.post("/materials", body);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (err) {
      toast.error("Failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this material?")) return;
    await api.delete(`/materials/${id}`);
    load();
  };

  const toggleAllergen = (a) =>
    setForm((f) => ({
      ...f,
      allergens: f.allergens.includes(a)
        ? f.allergens.filter((x) => x !== a)
        : [...f.allergens, a],
    }));

  return (
    <div>
      <PageHeader title="Raw Materials" eyebrow="Inbound · Stock control">
        <DataIOToolbar resource="materials" label="Materials" onImported={load} />
        <Button onClick={startNew} className="h-12 px-5 gap-2" data-testid="new-material-button">
          <Plus className="h-5 w-5" /> New material
        </Button>
      </PageHeader>

      <Panel
        title={`Materials (${filteredRows.length}${search ? ` of ${rows.length}` : ""})`}
        action={
          <div className="relative w-64 max-w-full">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, SKU or category…"
              className="h-10 pl-9"
              data-testid="materials-search"
            />
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="materials-table">
            <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
              <tr>
                <th className="py-3">Name</th>
                <th>SKU</th>
                <th>Category</th>
                <th>Unit</th>
                <th>Allergens</th>
                <th className="text-right">Stock</th>
                <th className="text-right">Min</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => {
                const low = r.min_stock > 0 && r.stock < r.min_stock;
                return (
                  <tr key={r.id} className="border-t border-border h-16">
                    <td className="font-medium">{r.name}</td>
                    <td className="font-mono text-sm">{r.sku || "—"}</td>
                    <td className="text-sm text-stone-600">{r.category || "—"}</td>
                    <td>{r.unit}</td>
                    <td><AllergenTags items={r.allergens} /></td>
                    <td className={`text-right tnum font-semibold ${low ? "text-amber-700" : ""}`}>
                      {fmt2(r.stock, "0.00")}
                    </td>
                    <td className="text-right tnum text-stone-500">{fmt2(r.min_stock, "0.00")}</td>
                    <td className="flex gap-1 justify-end">
                      <Button size="icon" variant="ghost" onClick={() => startEdit(r)} data-testid={`edit-material-${r.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(r.id)} data-testid={`delete-material-${r.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {filteredRows.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="py-10 text-center text-stone-500"
                    data-testid="materials-empty"
                  >
                    {rows.length === 0
                      ? "No materials defined yet."
                      : `No materials match “${search}”.`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="material-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit" : "New"} raw material</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input className="h-12 mt-1" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} required data-testid="material-name" />
              </div>
              <div>
                <Label>SKU</Label>
                <Input className="h-12 mt-1" value={form.sku} onChange={(e)=>setForm({...form, sku: e.target.value})} data-testid="material-sku" />
              </div>
              <div>
                <Label>Unit</Label>
                <Input className="h-12 mt-1" value={form.unit} onChange={(e)=>setForm({...form, unit: e.target.value})} required data-testid="material-unit" />
              </div>
              <div>
                <Label>Minimum stock</Label>
                <Input type="number" step="0.01" className="h-12 mt-1" value={form.min_stock} onChange={(e)=>setForm({...form, min_stock: e.target.value})} data-testid="material-min" />
              </div>
              <div className="col-span-2">
                <Label>Category</Label>
                <Input
                  className="h-12 mt-1"
                  value={form.category}
                  onChange={(e)=>setForm({...form, category: e.target.value})}
                  placeholder="e.g. Flour, Sugar, Yeast — used for batch-time substitution"
                  data-testid="material-category"
                />
                <p className="text-xs text-stone-500 mt-1">
                  Materials sharing a category can be substituted at batch time
                  if the category is enabled in Admin → Settings.
                </p>
              </div>
            </div>
            <div>
              <Label>Allergens</Label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="allergen-picker">
                {ALLERGENS.map((a)=>(
                  <button type="button" key={a} onClick={()=>toggleAllergen(a)} className={`px-3 h-9 rounded-full border text-sm font-medium ${form.allergens.includes(a) ? "bg-amber-100 border-amber-400 text-amber-900" : "bg-white border-stone-300 hover:bg-stone-50"}`}>
                    {a}
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-border dark:border-stone-700 p-4 space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-600 dark:text-stone-300 font-semibold">
                  Default pack size for goods intake
                </Label>
                <p className="text-xs text-stone-500 mt-1">
                  Optional. Flour usually arrives as 1 × 16 kg bag — set this and Goods Intake will prefill it for you.
                </p>
              </div>
              <div className="grid grid-cols-12 gap-2 items-center">
                <Input
                  type="number" step="1" min="0"
                  className="col-span-3 h-12"
                  value={form.default_pack_count}
                  onChange={(e)=>setForm({...form, default_pack_count: e.target.value})}
                  placeholder="Count"
                  data-testid="material-default-pack-count"
                />
                <Select
                  value={form.default_pack_type || "none"}
                  onValueChange={(v)=>setForm({...form, default_pack_type: v === "none" ? "" : v})}
                >
                  <SelectTrigger className="col-span-3 h-12" data-testid="material-default-pack-type">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {PACK_TYPES.map((p) => (
                      <SelectItem key={p} value={p}>{p}{p === "unit" ? "" : "s"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="col-span-1 text-center text-stone-500 text-sm">×</span>
                <Input
                  type="number" step="0.01" min="0"
                  className="col-span-3 h-12"
                  value={form.default_pack_size}
                  onChange={(e)=>setForm({...form, default_pack_size: e.target.value})}
                  placeholder="Size"
                  data-testid="material-default-pack-size"
                />
                <Select
                  value={form.default_pack_unit || form.unit || "kg"}
                  onValueChange={(v)=>setForm({...form, default_pack_unit: v})}
                >
                  <SelectTrigger className="col-span-2 h-12" data-testid="material-default-pack-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["kg", "g", "l", "ml", "unit"].map((u) => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button type="submit" className="h-12 px-6" data-testid="material-submit">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
